import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
  title?: string;
  size?: "sm" | "md";
  className?: string;
  /** When true the dropdown panel uses a transparent dark background (e.g. over video). */
  overlay?: boolean;
  placeholder?: string;
}

export function Select({
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel,
  title,
  size = "md",
  className = "",
  overlay = false,
  placeholder,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const buttonId = useId();

  const selected = options.find((o) => o.value === value);
  const displayLabel = selected?.label ?? placeholder ?? "";
  const sizeClasses =
    size === "sm" ? "px-2.5 py-1 text-xs gap-1.5" : "px-3.5 py-2 text-sm gap-2";

  const close = useCallback(() => {
    setOpen(false);
    setActiveIdx(-1);
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, close]);

  // Scroll active option into view.
  useEffect(() => {
    if (!open || activeIdx < 0 || !listRef.current) return;
    const el = listRef.current.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  function toggle() {
    if (disabled) return;
    setOpen((o) => {
      if (!o) {
        const idx = options.findIndex((o) => o.value === value);
        setActiveIdx(idx >= 0 ? idx : 0);
      }
      return !o;
    });
  }

  function selectOption(val: string) {
    onChange(val);
    close();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === "Escape") {
      close();
      return;
    }
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        const idx = options.findIndex((o) => o.value === value);
        setActiveIdx(idx >= 0 ? idx : 0);
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (activeIdx >= 0 && activeIdx < options.length) {
        selectOption(options[activeIdx].value);
      }
    }
  }

  const panelClass = overlay
    ? "bg-black/90 border-white/20 text-white"
    : "bg-brand-surface border-brand-line text-brand-text shadow-2xl";

  const triggerBase = overlay
    ? "bg-black/70 border-white/20 text-white hover:bg-black/90"
    : "bg-brand-surface border-brand-line text-brand-text hover:border-brand-red";

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        id={buttonId}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={title}
        className={`inline-flex items-center justify-between rounded-lg border outline-none transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${triggerBase} ${sizeClasses} ${
          open && !overlay ? "border-brand-red" : ""
        }`}
      >
        <span className="truncate text-left flex-1">{displayLabel}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>

      {open && (
        <div
          ref={listRef}
          role="listbox"
          aria-labelledby={buttonId}
          className={`absolute z-50 top-full mt-1 right-0 min-w-full rounded-lg border overflow-hidden max-h-60 overflow-y-auto ${panelClass}`}
        >
          {options.map((opt, i) => {
            const isActive = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isActive}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => selectOption(opt.value)}
                className={`w-full text-left flex items-center gap-2 transition-colors ${
                  size === "sm" ? "px-2.5 py-1.5 text-xs" : "px-3.5 py-2 text-sm"
                } ${
                  i === activeIdx
                    ? overlay
                      ? "bg-white/15"
                      : "bg-brand-hover"
                    : ""
                } ${isActive ? "font-semibold" : "font-normal"} ${
                  overlay ? "text-white hover:bg-white/10" : "text-brand-text hover:bg-brand-hover"
                }`}
              >
                <span
                  className={`shrink-0 w-1.5 h-1.5 rounded-full transition-colors ${
                    isActive
                      ? overlay
                        ? "bg-white"
                        : "bg-brand-red"
                      : "bg-transparent"
                  }`}
                />
                <span className="truncate flex-1">{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}